import { useState, useEffect, useRef } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Upload, X, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

export default function JourneyPhotos({ token }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const res = await safeFetch(`${API}/journey-photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPhotos(data);
      }
    } catch (err) {
      console.error("Failed to fetch photos:", err);
    }
    setLoading(false);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      // Upload file first
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await safeFetch(`${API}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!uploadRes.ok) {
        throw new Error("Upload failed");
      }

      const { url } = await uploadRes.json();

      // Create journey photo entry
      const res = await safeFetch(`${API}/journey-photos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          photo_url: url,
          caption: caption.trim() || ""
        })
      });

      if (res.ok) {
        toast.success("Photo added to your journey!");
        setCaption("");
        fetchPhotos();
      } else {
        throw new Error("Failed to save photo");
      }
    } catch (err) {
      toast.error("Failed to upload photo");
    }
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (photoId) => {
    if (!confirm("Are you sure you want to delete this photo?")) return;

    try {
      const res = await safeFetch(`${API}/journey-photos/${photoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success("Photo deleted");
        setPhotos(photos.filter(p => p.photo_id !== photoId));
        if (selectedPhoto?.photo_id === photoId) {
          setSelectedPhoto(null);
        }
      }
    } catch (err) {
      toast.error("Failed to delete photo");
    }
  };

  return (
    <div className="space-y-6" data-testid="journey-photos">
      {/* Upload Section */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <h3 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <Camera className="w-5 h-5 text-forest" />
          Add Journey Photo
        </h3>
        
        <div className="space-y-4">
          <Input
            data-testid="photo-caption-input"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Add a caption (optional)"
            className="mb-3"
          />
          
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="photo-file-input"
          />
          
          <Button
            data-testid="upload-photo-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full bg-forest hover:bg-forest-dark text-white py-6"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-2" />
                Choose Photo to Upload
              </>
            )}
          </Button>
          
          <p className="text-xs text-slate-500 text-center">
            Share your beautiful journey moments! Max 5MB, JPG/PNG/WEBP
          </p>
        </div>
      </div>

      {/* Photos Grid */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <h3 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-forest" />
          My Journey Photos ({photos.length})
        </h3>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-forest" />
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Camera className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No journey photos yet</p>
            <p className="text-sm mt-1">Upload your first travel memory!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div
                key={photo.photo_id}
                data-testid={`photo-${photo.photo_id}`}
                className="relative group aspect-square rounded-xl overflow-hidden bg-slate-100 cursor-pointer"
                onClick={() => setSelectedPhoto(photo)}
              >
                <img
                  src={photo.photo_url.startsWith("/") ? `${API.replace("/api", "")}${photo.photo_url}` : photo.photo_url}
                  alt={photo.caption || "Journey photo"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-3 left-3 right-3">
                    {photo.caption && (
                      <p className="text-white text-sm truncate">{photo.caption}</p>
                    )}
                    <p className="text-white/70 text-xs mt-1">
                      {new Date(photo.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  data-testid={`delete-photo-${photo.photo_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(photo.photo_id);
                  }}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center"
            onClick={() => setSelectedPhoto(null)}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={selectedPhoto.photo_url.startsWith("/") ? `${API.replace("/api", "")}${selectedPhoto.photo_url}` : selectedPhoto.photo_url}
            alt={selectedPhoto.caption || "Journey photo"}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {selectedPhoto.caption && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/70 text-white px-6 py-3 rounded-full text-sm">
              {selectedPhoto.caption}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
